"use client";
import { useEffect, useState } from "react";

import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import getPublicClient from "@/lib/algolia/getPublicClient";
import { ProductType as CourseType } from "@/common/course/types";
import logger from "@/lib/logger";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { AlgoliaHit } from "@/components/ProductSearch/types";
import { ProductSearchListItemProps } from "@/components/ProductSearch/components/v2/ProductSearchListItem";
import { mapAlgoliaHitsToListItems } from "./mapAlgoliaHitToListItem";
import {
  DEFAULT_QUERY_SUGGESTIONS_INDEX,
  DEFAULT_TYPING_STATE_STRATEGY,
  type TypingStateStrategy,
} from "@/monarch/rules/productSearch/types";
import { getSearchQueryHints, type QueryHint } from "./getSearchQueryHints";

export type Category = QueryHint;

export interface AutocompleteResults {
  products: ProductSearchListItemProps[];
  categories: Category[];
  hasError: boolean;
  isLoading: boolean;
}

const getProductHits = async (
  client: any,
  searchParams: { [key: string]: any },
) => {
  const params = { ...searchParams };
  const { hits, queryID } = await client.searchSingleIndex({
    indexName: PRODUCT_INDEX_EN,
    searchParams: params,
  });
  return hits.map((hit: AlgoliaHit) => ({ ...hit, queryID }));
};

const processAutocompleteHits = (productHits: AlgoliaHit[]) => {
  // Prioritize Executive Education products (up to 2)
  const executiveEducation = productHits
    .filter((hit) => hit.product === CourseType.EXECUTIVE_EDUCATION)
    .slice(0, 2);

  const generalHits = productHits.filter(
    (hit) => hit.product !== CourseType.EXECUTIVE_EDUCATION,
  );

  const processedHits = [...executiveEducation, ...generalHits];
  return processedHits.slice(0, 20);
};

const handleError = (error: Error) => {
  const message = `Failed to fetch autocomplete results from Algolia`;
  logger.error(message, error);
};

/**
 * Hook for Algolia predictive search and autocomplete
 * Returns both product results and query hints based on the search query
 *
 * @param query - The search query string
 * @returns Object containing products array and query hints
 */
const useAlgoliaAutocomplete = (
  query: string,
  typingStateStrategy: TypingStateStrategy = DEFAULT_TYPING_STATE_STRATEGY,
  querySuggestionsIndex?: string,
): AutocompleteResults => {
  const client = getPublicClient();
  const [results, setResults] = useState<AutocompleteResults>({
    products: [],
    categories: [],
    hasError: false,
    isLoading: false,
  });
  const indexName = PRODUCT_INDEX_EN as string;

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery) {
      let cancelled = false;
      setResults((current) => ({
        ...current,
        isLoading: true,
        hasError: false,
      }));

      const productSearchParams = {
        query: trimmedQuery,
        clickAnalytics: true,
        restrictSearchableAttributes: [
          getFacetAttributeName("title", { indexName }),
        ],
        attributesToRetrieve: [
          // Product index fields
          getFacetAttributeName("title", { indexName }),
          getFacetAttributeName("marketing_url", { indexName }),
          getFacetAttributeName("partner_keys", { indexName }),
          getFacetAttributeName("uuid", { indexName }),
          getFacetAttributeName("owners", { indexName }),
          getFacetAttributeName("weeks_to_complete", { indexName }),
          getFacetAttributeName("program_type", { indexName }),
          getFacetAttributeName("subject", { indexName }),
          // Product summary index fields
          getFacetAttributeName("productName", { indexName }),
          getFacetAttributeName("productSlug", { indexName }),
          getFacetAttributeName("productUuid", { indexName }),
          getFacetAttributeName("partnerKeys", { indexName }),
          getFacetAttributeName("partnerLogoUrl", { indexName }),
          getFacetAttributeName("weeksToComplete", { indexName }),
          getFacetAttributeName("partnerLogoOverride", { indexName }),
          getFacetAttributeName("partnerName", { indexName }),
          getFacetAttributeName("attributes", { indexName }),
          getFacetAttributeName("productType", { indexName }),
          getFacetAttributeName("level", { indexName }),
          getFacetAttributeName("flexibility", { indexName }),
          getFacetAttributeName("skills", { indexName }),
          getFacetAttributeName("skillsData", { indexName }),
          "product",
        ],
        highlightPreTag: `<em class="bg-transparent font-bold not-italic">`,
        highlightPostTag: "</em>",
        hitsPerPage: 20,
      };

      const fetchAutocomplete = async () => {
        try {
          const [productHits, categories] = await Promise.all([
            getProductHits(client, productSearchParams),
            getSearchQueryHints(
              client,
              trimmedQuery,
              indexName,
              typingStateStrategy,
              querySuggestionsIndex ?? DEFAULT_QUERY_SUGGESTIONS_INDEX,
            ),
          ]);

          const processedHits = processAutocompleteHits(productHits);
          const mappedProducts = mapAlgoliaHitsToListItems(processedHits);

          if (!cancelled) {
            setResults({
              products: mappedProducts,
              categories,
              hasError: false,
              isLoading: false,
            });
          }
        } catch (error) {
          if (cancelled) return;

          handleError(error as Error);
          setResults({
            products: [],
            categories: [],
            hasError: true,
            isLoading: false,
          });
        }
      };

      fetchAutocomplete();

      return () => {
        cancelled = true;
      };
    }

    setResults({
      products: [],
      categories: [],
      hasError: false,
      isLoading: false,
    });
  }, [query, client, indexName, typingStateStrategy, querySuggestionsIndex]);

  return results;
};

export default useAlgoliaAutocomplete;
